import { getMenuAuth } from "src/apis/authApi";
import { useCallback, useEffect } from "react";
import { getPUR2040_R01List, getPUR2040_R01AllCount, exportPUR2040_R01Excel } from "./Api_PUR2040_R01";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";
import useMenuGlobalStore from "src/store/useMenuStore";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";

import { usePUR2040_R01Store, defaultPUR2040_R01Filter, PUR2040_R01Filter } from "./Store_PUR2040_R01";
import { PUR2040_R01Dto } from "./Dto_PUR2040_R01";
import { fetchFilterConfig } from "src/utils/grid/GridHandlerContent";

interface PUR2040_R01ContentProps {
    setMessage: (message: string) => void;
}

export const Content_PUR2040_R01 = (props: PUR2040_R01ContentProps) => {
    const { setMessage } = props;

    const { 
        PUR2040_R01Filter, setPUR2040_R01Filter,
        PUR2040_R01PastFilter, setPUR2040_R01PastFilter,
        PUR2040_R01List, setPUR2040_R01List, 
        setAllCount,
        setScrollTop,
    } = usePUR2040_R01Store();
    const { setLoadingSpinner } = useLoadingSpinnerStore();
    const { selectedThirdMenu } = useMenuGlobalStore();
    const menuCdStore = selectedThirdMenu?.menuCd;
    const setAuth = useActionbarAuthStore((state) => state.setAuth);

    useFetchActionbarAuth("PUR2040_R01");

    useEffect(() => {
        getMenuAuth("PUR2040_R01").then((res) => {
            setAuth(res.data);
        });
    }, []);

    useEffect(() => {
        if (!menuCdStore) {
            console.log("[PUR2040_R01] menuCd가 없습니다. 메뉴를 선택하세요.");
        } else {
            console.log("[PUR2040_R01] menuCd:" + menuCdStore);
        }
    }, [menuCdStore]);

    const getParams = (filter: Partial<PUR2040_R01Filter>) => {
        return {
            compCd: filter.compCd,
            customCd: filter.customCd,
            itemCd: filter.itemCd,
            dateRange: filter.dateRange,
            listLength: filter.listLength,
            refetchListLength: filter.refetchListLength,
        };
    }

    const renewalAllCount = useCallback(async () => {
        const response = await getPUR2040_R01AllCount(getParams(PUR2040_R01Filter));
        setAllCount(response.data.data);
        return response.data.data;
    }, [PUR2040_R01Filter, setAllCount]);

    const fetchData = useCallback(async () => {
        setScrollTop(null);
        setLoadingSpinner(true);
        try {
            const response = await getPUR2040_R01List(
                '',
                getParams(PUR2040_R01Filter),
                PUR2040_R01List.length ?? 0
            );

            const rawData = (response.data.data as Omit<PUR2040_R01Dto, 'valueIndex'|'orderIndex'|'orderIndexSeq'>[]).map((row, idx) => ({
                valueIndex: idx,
                orderIndex: idx + 1,
                orderIndexSeq: 0,
                ...row,
            }));

            const newList = [...rawData] as PUR2040_R01Dto[];
            const processAllCount = await renewalAllCount();

            if (newList && newList.length > 0) {
                setPUR2040_R01PastFilter(PUR2040_R01Filter);
                setPUR2040_R01List(newList);
                ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
            } else {
                showWarning('데이터가 없습니다.');
                setPUR2040_R01List([]);
            }
        } catch (error) {
            console.error('데이터 조회 실패:', error);
            showWarning('데이터 조회에 실패했습니다.');
            setPUR2040_R01List([]);
        } finally {
            setLoadingSpinner(false);
        }
    }, [PUR2040_R01Filter, setPUR2040_R01List, setPUR2040_R01PastFilter, renewalAllCount, setMessage, setLoadingSpinner, setScrollTop]);
    
    const handleExcelClick = async () => {
        const {isDefaultFilter, isDefaultPastFilter, listLength} = fetchFilterConfig(
            PUR2040_R01Filter,
            PUR2040_R01PastFilter,
            defaultPUR2040_R01Filter,
            PUR2040_R01List.length
        );

        const response = await exportPUR2040_R01Excel(
            isDefaultFilter ? '' : 'search',
            getParams(PUR2040_R01Filter)
        );
        downloadSettingExcel({
            responseData: response.data.data,
            fileName: '매입내역 조회-excel.xlsx',
        });
    };

    return {
        fetchData,
        handleExcelClick,
    };
};
